import React, { use, useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Typography,
} from "@mui/material";
import CustomModal from "../../theme/style-components/CustomModal";
import { useGetEmployeeQuery } from "../../store/employee/EmployeeApiSlice";
import CustomButton from "../../theme/style-components/CustomButton";
import CustomInput from "../../theme/style-components/CustomInput";
import { useTheme } from "@emotion/react";
import { useShareCertificationMutation } from "../../store/certification/CertificationSlice";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectCurrentUserObject } from "../../store/auth/authSlice";

const AssignCertificateModal = ({
  isModalOpen,
  handleClose,
  selectedCertificateId,
}) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showTable, setShowTable] = useState(false);
   const user = useSelector(selectCurrentUserObject);

  const { data, isLoading, error } = useGetEmployeeQuery(
    { query: `org_id=${user?.company}${searchText ? `&search=${searchText}` : ""}` },
    { skip: !searchText },{skip: !user?.company}
  );

  const [shareCertification, { isLoading: isSharing, error: shareError }] =
    useShareCertificationMutation();

  const employeeData = data?.data || [];

  const handleSearchClick = () => {
    if (searchText.trim() === "") {
      setShowTable(false);
      return;
    }
    setShowTable(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText.trim() === "") {
        setShowTable(false);
      } else {
        setShowTable(true);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSelectEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((empId) => empId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedEmployees(employeeData.map((emp) => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedEmployees.length === 0) {
      return;
    }
    try {
      const response = await shareCertification({
        employee_ids: selectedEmployees,
        certificate_id: selectedCertificateId,
      })
        .unwrap()
        .then((res) => {
          toast.success("Certificate assigned successfully");
          handleClose();
        });
    } catch (err) {
      console.error("Error sharing certificate:", err);
    }
  };

  return (
    <CustomModal
      sx={{ Margin: "0" }}
      isShowActionButton={false}
      isModalOpen={isModalOpen}
      handleClose={handleClose}
      title="Assign Employee"
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 2,
          mb: 2,
          justifyContent: "center",
        }}
      >
        <CustomInput
          variant="outlined"
          placeholder="Search employees..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <CustomButton
          color="primary"
          type="button"
          buttonName="Search"
          onClick={handleSearchClick}
        />
      </Box>

      {showTable && !isLoading && (
        <>
          <TableContainer sx={{ height: "260px" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    fontWeight: "600",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#243E57" : "#dfe3e8",
                    whiteSpace: "nowrap",
                    fontSize: "13px",
                    lineHeight: " 1",
                    padding: "15px",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      onChange={handleSelectAll}
                      checked={
                        selectedEmployees.length > 0 &&
                        selectedEmployees.length === employeeData.length
                      }
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeeData.length > 0 ? (
                  employeeData.map((emp) => (
                    <TableRow
                      key={emp.id}
                      selected={selectedEmployees.includes(emp.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEmployees.includes(emp.id)}
                          onChange={() => handleSelectEmployee(emp.id)}
                        />
                      </TableCell>
                      <TableCell>{emp.name}</TableCell>
                      <TableCell>{emp.email}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body1" sx={{ py: 2, color: "gray" }}>
                        No data found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <CustomButton
              color="primary"
              type="button"
              buttonName="Cancel"
              onClick={handleClose}
            />
            <CustomButton
              color="primary"
              type="button"
              buttonName="Confirm"
              onClick={handleConfirmSelection}
              disabled={
                selectedEmployees.length === 0 || employeeData.length === 0
              }
            />
          </Box>
        </>
      )}
    </CustomModal>
  );
};

export default AssignCertificateModal;
